/**
 * NewPipelineModalView — pure presentation for the "+ New pipeline" creation
 * dialog (SE-5487), restyled to the Figma artboard (nodes 80:15750 title,
 * 80:15751 description, 80:15753 label, 80:15754 input, 80:15758 template card,
 * 80:15804 footer).
 *
 * One dialog combines the template picker and the name field:
 *   - A title and a one-line description of what the dialog is for.
 *   - A single name `Input`.
 *   - A grid of template cards (New Player Acquisitions / Academy Promotions /
 *     Outgoing Transfers / Custom-blank). Each card previews its seeded stages
 *     as wrapping chips; the selected card is highlighted.
 *   - `Cancel` and `Create pipeline`, the latter disabled until a name is
 *     entered (AC — empty name blocks creation).
 *
 * Nothing here is watchlist-related — there is no "create a Watchlist instead"
 * nudge (AC3). Every value (including each card's stable `onSelect`) is a prop,
 * so the container owns all state/behavior and no handler is created in JSX.
 *
 * ## Spacing
 *
 * 24px shell padding, and 16px between the dialog's sections. The artboard's
 * rhythm is 18px, which is off this repo's closed 4px grid (there is no 18 or
 * 4.5 rung, and off-scale spacing classes compile to nothing); 16 is the
 * nearest rung and 20 the next one up. Flagged rather than forced.
 *
 * ## Stage chips replaced the glossary lines
 *
 * Each stage used to render its name over a `tooltip` glossary line, because
 * the vendored Tooltip is web-only and an inline glossary was the
 * cross-platform way to explain a stage. The artboard draws stages as bare
 * chips, so the glossary text is no longer shown — `tooltip` is still carried
 * in the template data and is still the right source if the explanation comes
 * back as a real tooltip or a detail view.
 * @remarks View component — no business logic, hooks, or state.
 */

import { Search } from "lucide-react-native";
import { memo } from "react";

import type { PipelineTemplateOption } from "../../../constants/pipelineTemplates";

import { TONE_CLASS } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** A template option enriched with its selection state and stable handler. */
export interface PipelineTemplateCard extends PipelineTemplateOption {
  /** Whether this card is the selected one. */
  readonly isSelected: boolean;
  /** Stable selection handler (created once per render in the container). */
  readonly onSelect: () => void;
}

/** Props for {@link NewPipelineModalView}. */
export interface NewPipelineModalViewProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** Close request handler (backdrop / cancel). */
  readonly onClose: () => void;
  /** Template cards to render (each carries its own `onSelect` + `isSelected`). */
  readonly cards: readonly PipelineTemplateCard[];
  /** Current name field value. */
  readonly name: string;
  /** Name field change handler. */
  readonly onNameChange: (value: string) => void;
  /** Whether the Create button is enabled (name present, not already creating). */
  readonly canCreate: boolean;
  /** Whether a create is in flight. */
  readonly isCreating: boolean;
  /** Create handler. */
  readonly onCreate: () => void;
}

/**
 * Card chrome. The artboard draws every card with a 12px radius, 16px padding
 * and a pale 1px border; selection recolours the border to the accent and
 * tints the fill, which is this app's existing selected-card language.
 */
const CARD_BASE_CLASS =
  "min-w-0 grow basis-64 rounded-lg border p-4 web:transition-colors";
const CARD_SELECTED_CLASS = "border-accent-primary bg-surface-subtle";
const CARD_UNSELECTED_CLASS = "border-outline-subtle bg-surface-base";

/**
 * Stage-chip chrome. 10px label, 8x4 padding, small radius — the artboard's
 * chip is radius 2, which rounds to the 4px rung (the closed radius scale is
 * 0 / 4 / 8 / 12, so 4 is the only value below 8).
 */
const CHIP_BASE_CLASS = "shrink-0 rounded-sm px-2 py-1";
/** A stage that CLOSES the pipeline: outlined rather than filled. */
const CHIP_TERMINAL_CLASS = "border border-outline-strong bg-surface-base";
/** A stage the pipeline passes through. */
const CHIP_STEP_CLASS = "bg-surface-raised";

/**
 * Presentation for the "+ New pipeline" creation dialog.
 * @param props - {@link NewPipelineModalViewProps}
 * @param props.isOpen - Whether the dialog is visible
 * @param props.onClose - Close request handler
 * @param props.cards - Template cards to render
 * @param props.name - Current name field value
 * @param props.onNameChange - Name field change handler
 * @param props.canCreate - Whether the Create button is enabled
 * @param props.isCreating - Whether a create is in flight
 * @param props.onCreate - Create handler
 * @returns The dialog modal.
 */
const NewPipelineModalView = ({
  isOpen,
  onClose,
  cards,
  name,
  onNameChange,
  canCreate,
  isCreating,
  onCreate,
}: NewPipelineModalViewProps) => (
  <Modal
    isOpen={isOpen}
    onClose={isCreating ? undefined : onClose}
    closeOnOverlayClick
    size="xl"
  >
    <ModalBackdrop />
    {/* 24px on all four sides. `ModalContent` carries its own padding, so this
        overrides rather than nests — a second inset would stack. */}
    <ModalContent testID="new-pipeline-modal" className="!p-6">
      <ModalBody>
        <Stack className="gap-4">
          <Stack className="gap-1">
            {/* 20px Satoshi Bold (80:15750) — `title-lg` is exactly that
                size+family pair. */}
            <Text variant="title-lg" className="text-content-primary">
              New pipeline
            </Text>
            {/* 14px Satoshi Regular, mid grey (80:15751). */}
            <Text variant="body" className="text-content-muted">
              Give it a name and pick a recruitment-motion template. You can
              rename stages later.
            </Text>
          </Stack>

          <Stack className="gap-2">
            {/* 12px Satoshi MEDIUM, near-black (80:15753). The DPL has no
                bundled 12px-medium variant, so `caption` supplies the size and
                `font-medium` the SatoshiMedium face — both closed tokens. */}
            <Text
              variant="caption"
              className="font-medium text-content-primary"
            >
              Pipeline name
            </Text>
            {/* Radius 8 per 80:15754; the Input atom's outline variant is
                radius 4, so the closed `rounded-md` token overrides it. */}
            <Input
              variant="outline"
              size="md"
              className="rounded-md"
              leadingIcon={Search}
              leadingIconTone="muted"
              field={{
                testID: "new-pipeline-name-input",
                value: name,
                onChangeText: onNameChange,
                placeholder: "e.g. Summer targets",
                "aria-label": "Pipeline name",
              }}
            />
          </Stack>

          <Stack className="gap-2">
            <Text
              variant="caption"
              className="font-medium text-content-primary"
            >
              TEMPLATES
            </Text>
            <ScrollView className="max-h-96">
              {/* Two per row: each card is at least 256px and grows to share
                  the row, so the grid is 2-up at the dialog's width and folds
                  to 1-up if it is ever narrower — no fixed column count and no
                  arbitrary width. */}
              <Stack direction="horizontal" className="flex-wrap gap-4">
                {cards.map(card => (
                  <Pressable
                    key={card.template}
                    testID={`new-pipeline-template-${card.template}`}
                    onPress={card.onSelect}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: card.isSelected }}
                    accessibilityLabel={card.label}
                    className={`${CARD_BASE_CLASS} ${
                      card.isSelected
                        ? CARD_SELECTED_CLASS
                        : CARD_UNSELECTED_CLASS
                    }`}
                  >
                    <Stack className="gap-4">
                      <Stack className="gap-3">
                        <Stack
                          direction="horizontal"
                          className="items-center gap-3"
                        >
                          {/* 16px Satoshi Bold — `title-sm`. */}
                          <Text
                            variant="title-sm"
                            className="min-w-0 flex-1 text-content-primary"
                          >
                            {card.label}
                          </Text>
                          {card.isMostUsed ? (
                            <Box
                              className={`shrink-0 rounded-sm px-2 py-1 ${TONE_CLASS["info-soft"].box}`}
                            >
                              <Text
                                variant="micro"
                                className={TONE_CLASS["info-soft"].text}
                              >
                                MOST USED
                              </Text>
                            </Box>
                          ) : null}
                        </Stack>
                        <Text variant="body" className="text-content-muted">
                          {card.description}
                        </Text>
                      </Stack>

                      <Stack
                        direction="horizontal"
                        className="flex-wrap items-center gap-2"
                      >
                        {card.stages.length === 0 ? (
                          <Box
                            className={`${CHIP_BASE_CLASS} ${CHIP_TERMINAL_CLASS}`}
                          >
                            <Text
                              variant="micro"
                              className="text-content-secondary"
                            >
                              + Add stage
                            </Text>
                          </Box>
                        ) : (
                          card.stages.map(stage => (
                            <Box
                              key={stage.name}
                              className={`${CHIP_BASE_CLASS} ${
                                stage.isTerminal
                                  ? CHIP_TERMINAL_CLASS
                                  : CHIP_STEP_CLASS
                              }`}
                            >
                              <Text
                                variant="micro"
                                className="text-content-secondary"
                              >
                                {stage.name}
                              </Text>
                            </Box>
                          ))
                        )}
                      </Stack>
                    </Stack>
                  </Pressable>
                ))}
              </Stack>
            </ScrollView>
          </Stack>

          {/* Footer (80:15804): right-aligned, 12px apart, both 40 tall. */}
          <Stack
            direction="horizontal"
            className="items-center justify-end gap-3"
          >
            <Button
              label="Cancel"
              variant="outline"
              tone="secondary"
              size="md"
              onPress={onClose}
              isDisabled={isCreating}
              accessibilityLabel="Cancel"
            />
            {/* `accent`, not `primary`. The artboard fills this button with
                #007aff, which is this repo's `accent-primary` token exactly
                (rgb(0,122,255), with its own dark-mode value) — the product's
                blue CTA, established in SE-6233. Gluestack's `primary` action
                is `primary-500`, which is near-BLACK here, so the button was
                rendering dark against a design that asks for blue. */}
            <Button
              testID="new-pipeline-create"
              label="Create pipeline"
              variant="solid"
              tone="accent"
              size="md"
              onPress={onCreate}
              isDisabled={!canCreate}
              isLoading={isCreating}
              accessibilityLabel="Create pipeline"
            />
          </Stack>
        </Stack>
      </ModalBody>
    </ModalContent>
  </Modal>
);

NewPipelineModalView.displayName = "NewPipelineModalView";

export default memo(NewPipelineModalView);
